import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'

import store from '@/store'
import typedStore from '@/store/typedStore'
import { PATH_SUBROOT, DEFAULT_ACTIVE_VISUALISATION_TYPE } from '@/constants'
import { AppRank, NodeTypes, AppRanks, Path, TreeNode, VisualisationTypes, BubblesPopupDetails } from '@/types'

@Module({ dynamic: true, store, name: 'activeVisualisation', namespaced: true })
export default class ActiveVisualisation extends VuexModule {
  appRank: AppRank = AppRanks.PRIMARY
  bubblesPopupDetails: BubblesPopupDetails | undefined

  @Mutation
  setVisualisationAppRank (appRank: AppRank) {
    this.appRank = appRank
  }

  get visualisationAppRank (): AppRank {
    return this.appRank
  }

  get isPrimaryApp (): boolean {
    return this.visualisationAppRank === AppRanks.PRIMARY
  }

  get isSecondaryApp (): boolean {
    return this.visualisationAppRank === AppRanks.SECONDARY
  }

  get appLoaded () {
    let result: Boolean = false
    if (this.isPrimaryApp) {
      result = typedStore.primary.app.appLoaded
    } else if (this.isSecondaryApp) {
      result = typedStore.primary.app.appLoaded && typedStore.secondary.app.appLoaded
    }
    return result
  }

  get activeTabTreeNode (): TreeNode | false {
    const activeTab = typedStore.public.display.activeTab
    if (activeTab) {
      if (activeTab.path === PATH_SUBROOT) {
        return new TreeNode({ name: PATH_SUBROOT, path: PATH_SUBROOT })
      }
      return typedStore.visualisationData.treeRoot.search(activeTab.path)
    }
    return false
  }

  get visualisationType () {
    const activeTab = typedStore.public.display.activeTab
    if (this.isPrimaryApp && typedStore.primary.app.isConsolidatedApp && !activeTab) {
      return VisualisationTypes.NONE
    }

    if (activeTab && activeTab.nodeType === NodeTypes.CUSTOM) {
      return VisualisationTypes.CUSTOM
    }

    return this.visualisationStore.app.config?.visualisation?.type ?? DEFAULT_ACTIVE_VISUALISATION_TYPE
  }

  get isBubbles () {
    return this.visualisationStore.app.isVisualisationApp && this.visualisationType === VisualisationTypes.BUBBLES
  }

  get isSunburst () {
    return this.visualisationStore.app.isVisualisationApp && this.visualisationType === VisualisationTypes.SUNBURST
  }

  get isCustom () {
    return this.visualisationType === VisualisationTypes.CUSTOM
  }

  get displaysTreeData () {
    switch (this.visualisationType) {
      case VisualisationTypes.BUBBLES:
      case VisualisationTypes.SUNBURST:
        return true
      default:
        return false
    }
  }

  get storyPath (): Path {
    return this.isSecondaryApp ? typedStore.public.display.secondaryStoryPath : typedStore.public.display.storyPath
  }

  get leafLevel (): number {
    return this.visualisationStore.app.levels.maxLevel
  }

  get visualisationStore () {
    if (this.isSecondaryApp) {
      return typedStore.secondary
    }
    return typedStore.primary
  }

  get visibleTabCount () {
    return typedStore.primary.entities.tabs.visibleTabs.length
  }

  get visibleTabHeight () {
    const height = typedStore.public.size.contentHeight
    const numberOfTabs = this.visibleTabCount
    return height / numberOfTabs
  }

  @Action({ rawError: true })
  updateStoryPath (payload: string|Path) {
    const path = payload.toString()
    if (this.isPrimaryApp) {
      typedStore.public.display.updatePrimaryStoryPath(path)
    } else {
      typedStore.public.display.updateSecondaryStoryPath({ primaryStoryPath: typedStore.public.display.activeTab!.secondaryAppPath!, secondaryStoryPath: path })
    }
  }

  @Mutation
  setActiveBubblesPopupDetails (popupDetails: BubblesPopupDetails | undefined) {
    this.bubblesPopupDetails = popupDetails
  }

  get activeBubblesPopupDetails (): BubblesPopupDetails | undefined {
    return this.bubblesPopupDetails
  }
}

export const ActiveVisualisationModule = getModule(ActiveVisualisation)
